import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getListingOrganisations,
  addListingOrganisation,
  removeListingOrganisation,
} from '../../../../apis/clinics/requests';
import { Organisation } from '../../../../types/organisation';
import { useSnackbar } from 'notistack';
import { FulfilmentTypeEnum } from '../../../../apis/clinics/types';

interface UseListingOrganisationsReturn {
  organisations: Organisation[];
  totalCount: number;
  isLoading: boolean;
  addOrganisation: (organisationId: string) => Promise<Organisation>;
  removeOrganisation: (organisationId: string, clinicFulfilmentType: FulfilmentTypeEnum) => Promise<void>;
}

const useListingOrganisations = (clinicId: string, page: number, limit: number): UseListingOrganisationsReturn => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const { data, isLoading } = useQuery(
    ['listingOrganisations', clinicId, page, limit],
    () => getListingOrganisations(clinicId, { page, limit }),
  );

  const addMutation = useMutation(
    (organisationId: string) => addListingOrganisation(clinicId, organisationId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['listingOrganisations', clinicId]);
        enqueueSnackbar(`Organisation has been added to listing organisations`, { variant: 'success' });
      }
    }
  );

  const removeMutation = useMutation(
    (organisationId: string) => removeListingOrganisation(clinicId, organisationId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['listingOrganisations', clinicId]);
      }
    }
  );

  const handleRemoveOrganisation = async (organisationId: string, clinicFulfilmentType: FulfilmentTypeEnum) => {
    const currentOrganisations = data?.data || [];
    if (clinicFulfilmentType === FulfilmentTypeEnum.WITHIN_LISTING_ORGANISATION_NETWORK && currentOrganisations.length <= 1) {
      enqueueSnackbar('Cannot remove the last listing organisation while "Within Listing Organisation Network" is selected in Fulfilment.', { variant: 'error' });
      return;
    }
    try {
      await removeMutation.mutateAsync(organisationId);
      enqueueSnackbar('Organisation has been removed from listing organisations', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar('Failed to remove organisation', { variant: 'error' });
    }
  };

  return {
    organisations: data?.data || [],
    totalCount: data?.pagination?.total || 0,
    isLoading,
    addOrganisation: addMutation.mutateAsync,
    removeOrganisation: handleRemoveOrganisation
  };
};

export default useListingOrganisations; 
